<template>

<app-content-body>

    <app-content-alert v-if="isApproval" theme="yellow" :is-inline="true" icon="warning">

        Please note this room requires all submissions to be approved before being scheduled.

    </app-content-alert>

	<app-content-box class="wizard" :is-grid="true">

		<div class="wizard-basic">

			<div class="wizard-basic-title">Quick start</div>

			<div class="wizard-basic-text">Get your event submitted in moments with the most common settings - you can still further customize and change things after submission!</div>

			<app-input-suggest label="Game" v-on:textchange="onGameNameChange" placeholder="Search game by name" image="image" v-model="model.game" api="bgg/game" :validation="$v.model.game" />
            <app-input-toggle label="Total players" :full-width="true" :slim="true" v-model="model.seats.total" :validation="$v.model.seats.total" :options="seatOptionsReduced" />
			<app-input-toggle label="Are you playing?" :full-width="true" v-model="model.seats.host" :validation="$v.model.seats.host" :options="hostOptions" />
            <app-input-scheduletime v-if="!isApproval" v-on:picker="is.picking = $event" :has-picker="room.spaces" :room="room" label="Time" v-model="model" :is-custom="true" v-on:space="model.space = $event" v-on:day="model.day = $event" v-on:time="model.time = $event" v-on:duration="model.duration = $event" />

            <template v-if="isApproval">
                <app-input-scheduletime v-on:picker="is.picking = $event" :has-picker="room.spaces" :room="room" :label="'Preferred Time #' + (index + 1)" v-model="model.preferred[index]" :key="index" v-for="(preferred, index) in model.preferred" />
            </template>

		</div>

		<div class="wizard-advanced">

			<i class="wizard-advanced-icon fa-duotone fa-user-helmet-safety" />

			<div class="wizard-advanced-title">Advanced setup</div>

			<div class="wizard-advanced-text">You can customize your event further including custom name and description, blocked seats, teacher wanted status, and more. You can also further change things after the event has been scheduled.</div>

			<app-button theme="darkblue" class="wizard-advanced-button" v-on:click="onAdvancedClick" v-if="is.advanced">Hide advanced options</app-button>
			<app-button theme="darkblue" class="wizard-advanced-button" v-on:click="onAdvancedClick" v-if="!is.advanced">Show advanced options</app-button>

		</div>

	</app-content-box>

	<app-content-box icon="settings" title="Setup" v-if="is.advanced">
			
		<app-input-toggle label="Official" v-model="model.official" notes="Is the event labelled as an official event." :validation="$v.model.official" v-if="$isModerator" />
		<app-input-toggle label="Enable Clashing" v-model="model.clashes.enabled" :validation="$v.model.clashes.enabled" v-if="$isModerator" notes="Turn off clashing if this is an event that attendees can participate in at any time." />
		<app-input-select label="Ticketed" v-model="model.ticket" notes="Is a ticket needed to take a seat on this event." :allow-deselect="true" placeholder="Not ticketed" :validation="$v.model.ticket" v-if="$isModerator && $util.count(references.tickets)" :options="references.tickets" />
		<app-input-select label="Type" placeholder="Select type" v-model="model.type" :validation="$v.model.type" :options="typeOptions" v-if="references.types.length" />
		<app-input-text label="Name" placeholder="Enter name..." v-model="model.name" :validation="$v.model.name" :maxlength="128" />
		<app-input-text v-if="$isModerator" label="Custom Host" maxlength="32" :toggle="model.host.enabled" v-on:toggle="model.host.enabled = !model.host.enabled" notes="Customise the name of the host to something other than the user that created the event." placeholder="Enter name..." v-model="model.host.name" :validation="$v.model.host.name" />
		<app-input-text label="Description" :autogrow="true" placeholder="Enter description..." v-model="model.description" :validation="$v.model.description" />
		<app-input-image label="Image" v-model="model.image" :validation="$v.model.image" notes="Image used must be relevant to the event and be appropriate for all ages." />
		<app-input-toggle :full-width="true" :label="(model.ticket) ? 'Capacity' : 'Total seats'" :slim="true" v-model="model.seats.total" :validation="$v.model.seats.total" :options="seatOptions" :notes="(model.ticket) ? 'The total number of seats available.' : 'The total number of players.'" />
		<app-input-text label="Enter number" v-model="model.seats.totalOther" :validation="$v.model.seats.totalOther" notes="Must be a number between 1 and 1000." v-if="model.seats.total === 1001" />
		<app-input-toggle :full-width="true" label="Blocked seats" :slim="true" v-model="model.seats.blocked" :validation="$v.model.seats.blocked" :options="blockOptions" notes="The number of seats blocked for other people not on this system." v-if="model.seats.total <= 8 && hasGame" />
		<app-input-text label="Blocked seats" v-model="model.seats.blockedOther" :validation="$v.model.seats.blockedOther" notes="Must be a number between 0 and 1000." v-if="model.seats.total > 8 && model.seats.total !== 1002 && hasGame" />
		<app-input-toggle :full-width="true" label="Min. Age" :slim="true" v-model="model.age" :validation="$v.model.age" :options="ageOptions" />
		<app-input-checklist :collapsed="!isNew" fullWidth="true" :singular="true" :columns="1" v-if="hasGame" label="Min. Experience" v-model="model.experience" :validation="$v.model.experience" :options="$constants.schedule.event.experienceName" :descriptions="$constants.schedule.event.experienceDescription" />
		<app-input-checklist :collapsed="!isNew" fullWidth="true" :singular="true" :columns="1" v-if="hasGame" label="Teacher Wanted" v-model="model.teacher" :validation="$v.model.teacher" :options="$constants.schedule.event.teacherName" :descriptions="$constants.schedule.event.teacherDescription" />
		<app-input-checklist v-if="references.lobbies.length" :max="lobbyLimit" label="Lobbies" notes="Select lobbies to help make your events easier to find." v-model="model.lobbies" :validation="$v.model.lobbies" :options="lobbyOptions" />

	</app-content-box>
        
	<app-button text="Submit" :disabled="!isValid || !isValidTime" v-on:click="onSaveClick" :loading="is.saving" />

    <app-spaces v-if="is.picking" v-on:close="is.picking = false" :is-picking="is.picking" :room="room" :spaces="references.spaces" :scheduled="references.scheduled" />

</app-content-body>

</template>

<script>

import mixForm from '@/mixin/form'
import { required, requiredIf } from 'vuelidate/lib/validators'

export default {

	mixins: [mixForm],

	props: ['room', 'clone'],

	data: function() {

		return {
			standalone: true,
            api: 'convention/schedule/submit/form',
			is: {
				picking: false,
				advanced: false
			},
			references: {
				tickets: [],
				types: [],
				days: [],
				times: [],
				spaces: [],
				lobbies: [],
				clashing: [],
                scheduled: []
			},
			params: {
				room: ''
			},
			hostOptions: {
				0: 'Yes, I am one of the players',
				1: 'No, I\'m hosting/teaching/organizing'
			},
			seatOptions: {
				1: 1,
				2: 2,
				3: 3,
				4: 4,
				5: 5,
				6: 6,
				7: 7,
				8: 8,
				1001: 'Other',
				1002: 'Unlimited'
			},
			seatOptionsReduced: {
				1: 1,
				2: 2,
				3: 3,
				4: 4,
				5: 5,
				6: 6,
				7: 7,
				8: 8
			},
			model: {
				official: 0,
				type: 0,
				ticket: '',
				name: '',
				image: '',
				teacher: 0,
				age: 0,
				game: 0,
				experience: 0,
				description: '',
				lobbies: [],
                preferred: [
                    { space: '', day: 0, time: 0, duration: 0},
                    { space: '', day: 0, time: 0, duration: 0},
                    { space: '', day: 0, time: 0, duration: 0}
                ],
				host: {
					enabled: 0,
					name: ''
				},
				seats: {
					host: 0,
					total: 4,
					totalOther: 0,
					blocked: 0,
					blockedOther: 0
				},
				clashes: {
					enabled: 1
				},
				day: 0,
				time: 0,
				duration: 0,
				location: {
					skip: 0,
					room: 0,
					space: 0,
					custom: 0,
					directions: ''
				}
			}
		}

	},

	validations: {
		model: {
			host: {},
			name: {
				required
			},
			game: {
				requiredIf: requiredIf(function() {
				
					return !this.model.name

				})
			},
			type: {
				required
			},
			clashes: {},
			seats: {
				total: {
					required
				},
				totalOther: {
					// if total = 1001
				}
			},
			location: {
				skip: {},
                room: {},
				space: {},
				custom: {},
				directions: {}
			}
		}
	},

	computed: {

        isValidTime: function() {
        
            if (this.isApproval) {

                var count = 0

                this.$_.each(this.model.preferred, function(preferred) {
                
                    if(preferred.day && preferred.duration && preferred.time && (preferred.space || !this.hasSpaces)) {
                    
                        count++
                    
                    }

                }.bind(this))

                return count === 3
            
            } else {

                return this.model.day && this.model.duration && this.model.time && (this.model.space || !this.hasSpaces)
            
            }

        },

        hasSpaces: function() {
        
            return this.room.spaces

        },

		isApproval: function() {
		
			return this.room.approval
		
		},

		lobbyLimit: function() {

			return (this.$settings.schedule.events.submit.lobbies.max.enabled) ? this.$settings.schedule.events.submit.lobbies.max.limit : false

		},

		selectedType: function() {

			return (this.model.type) ? this.$_.findWhere(this.references.types, {
				id: this.model.type
			}) : false

		},

		hasGame: function() {

			return (this.selectedType) ? this.selectedType.games.enabled : true

		},

		blockOptions: function() {

			var seats = {}
			var total = (this.model.seats.total > 8) ? this.model.seats.totalOther || 8 : this.model.seats.total || 8

			for (var i = 0; i <= total; i++) {

				seats[i] = i

			}

			return seats
		},

		typeOptions: function() {

			var options = {}

			this.$_.each(this.references.types, function(type) {

				options[type.id] = type.name

			})

			return options

		},

		lobbyOptions: function() {

			var options = {}

			this.$_.each(this.references.lobbies, function(type) {

				options[type.id] = type.name

			})

			return options

		},

		ageOptions: function() {

			return this.$constants.schedule.ageShort

		},

		gameChosen: function() {

			return this.model.game

		}

	},

	watch: {

		gameChosen: function() {

			this.model.image = (this.model.game) ? 'https://img.geekgroup.app/api/games/avatar/' + this.model.game : ''

		}

	},

	methods: {

		onModelUpdate: function() {
		
			this.model.room = this.room.id
			this.model.preferred = [
                { space: '', day: 0, time: 0, duration: 0},
                { space: '', day: 0, time: 0, duration: 0},
                { space: '', day: 0, time: 0, duration: 0}
            ]

		},

		onCreateNew: function(params) {
		
			params.room = this.room.id
			params.clone = this.clone

			return params
		
		},

		onAdvancedClick: function() {
		
			this.is.advanced = !this.is.advanced

		},

		onLoadFailed: function() {

			if (this.$settings.schedule.events.submit.enabled == this.$constants.schedule.settings.events.submit.enabled.hidden) {

				this.$router.push({
					name: 'Dashboard'
				})

			} 

		},

		onSaved: function(json) {

            if(json.event) {

                this.$router.push({
                    name: 'Convention.Schedule.Event',
                    params: {
                        id: json.event
                    }
                })
            
            } else {
            
                this.$router.push({
                    name: 'Convention.Schedule.Suggestions'
                })
            
            }

		},

		onGameNameChange: function(name) {

			this.model.name = name

		}

	}

}

</script>

<style scoped>

.wizard >>> .box-content {
	grid-template-columns: auto 320px;
}

.wizard-basic {
    padding: 10px;
}

.wizard-basic-title {
	font-size: 20px;
	font-weight: 500;
	margin-bottom: 10px;
	color: #687589;
}

.wizard-basic-text {
	font-size: 14px;
	line-height: 18px;
	font-weight: 400;
	margin-bottom: 20px;
	color: #687589;
}

.wizard-advanced {
	background-color: #4083d3;
	color: #fff;
	padding: 20px;
	border-radius: 10px;
	text-align: center;
}

.wizard-advanced-icon {
	font-size: 64px;
	margin-bottom: 20px;
}

.wizard-advanced-title {
	font-size: 32px;
	font-weight: 500;
	margin-bottom: 20px;
}

.wizard-advanced-text {
	font-size: 14px;
	line-height: 18px;
	font-weight: 400;
}

.wizard-advanced-button {
	width: 100%;
	margin-top: 20px;
}

.location-empty {
	font-size: 14px;
	line-height: 20px;
	margin-top: 10px;
	color: #707070;
	text-align: center;
	border: 1px solid #eee;
	padding: 20px;
	border-radius: 4px;
}

.location-none {
	font-size: 14px;
	line-height: 20px;
	margin-top: 10px;
	color: #fff;
	text-align: center;
	border: 1px solid #eee;
	padding: 20px;
	background-color: #f65d5d;
	border-radius: 4px;
}

.location-notice {
	font-size: 14px;
	line-height: 20px;
	margin-top: 10px;
	color: #fff;
	text-align: center;
	border: 1px solid #eee;
	padding: 20px;
	background-color: #4082d3;
	border-radius: 4px;
}

.is-device .location-empty,
.is-device .location-none,
.is-device .location-notice {
	margin-top: 20px;
	padding: 20px;
}

</style>